import type { Vec2 } from '../../../geo/types';
import type { GateBlock } from './bespokeGate';

/**
 * Where a town wall turns at the face of the gate block it runs into, the piece left beyond the cut
 * can be too short for its turn, and its mitre would fold its sides over each other. Such a piece
 * starts inside the block on its next leg instead, far enough in that both corners of its square end
 * are buried `overlap` deep, so no gap opens between it and the face.
 */

function toLocal(p: Vec2, block: GateBlock): Vec2 {
  const [c, s] = [Math.cos(block.heading), Math.sin(block.heading)];
  const [dx, dy] = [p[0] - block.centre[0], p[1] - block.centre[1]];
  return [dx * c + dy * s, -dx * s + dy * c];
}

function inside(p: Vec2, block: GateBlock): boolean {
  const [x, y] = toLocal(p, block);
  return Math.abs(x) <= block.half[0] + 1e-6 && Math.abs(y) <= block.half[1] + 1e-6;
}

/* The L range over which a + L·b stays within [-h, h]; b = 0 keeps all of it or none. */
function within(a: number, b: number, h: number): [number, number] {
  if (Math.abs(b) < 1e-12) return Math.abs(a) <= h ? [-Infinity, Infinity] : [Infinity, -Infinity];
  const [l0, l1] = [(-h - a) / b, (h - a) / b];
  return [Math.min(l0, l1), Math.max(l0, l1)];
}

/**
 * The least distance L >= overlap back from `bend` against `ahead` at which a square end of the given
 * half width lies inside the block shrunk by `overlap`, or null if none does.
 */
export function leadIn(
  bend: Vec2,
  ahead: Vec2,
  halfWidth: number,
  block: GateBlock,
  overlap: number
): number | null {
  const [c, s] = [Math.cos(block.heading), Math.sin(block.heading)];
  const d: Vec2 = [ahead[0] * c + ahead[1] * s, -ahead[0] * s + ahead[1] * c];
  const n: Vec2 = [-d[1], d[0]];
  const p = toLocal(bend, block);
  let [lo, hi] = [overlap, Infinity];
  for (const side of [-1, 1]) {
    for (const axis of [0, 1] as const) {
      const corner = p[axis] + side * halfWidth * n[axis];
      const [a, b] = within(corner, -d[axis], block.half[axis] - overlap);
      [lo, hi] = [Math.max(lo, a), Math.min(hi, b)];
    }
  }
  return lo <= hi ? lo : null;
}

function unit(a: Vec2, b: Vec2): Vec2 {
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
  return [(b[0] - a[0]) / len, (b[1] - a[1]) / len];
}

/* A mitred turn pulls the inner side back from the bend by the half width times tan(turn / 2). */
function folds(start: Vec2, bend: Vec2, next: Vec2, halfWidth: number): boolean {
  const stub = Math.hypot(bend[0] - start[0], bend[1] - start[1]);
  if (stub < 1e-9) return true;
  const [a, b] = [unit(start, bend), unit(bend, next)];
  const turn = Math.acos(Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1])));
  return stub < halfWidth * Math.tan(Math.min(turn, Math.PI * 0.49) / 2);
}

function abutStart(
  points: readonly Vec2[],
  blocks: readonly GateBlock[],
  halfWidth: number,
  overlap: number
): Vec2[] {
  const [start, bend, next] = [points[0]!, points[1]!, points[2]];
  const block = blocks.find((b) => inside(start, b));
  if (!next || !block || !folds(start, bend, next, halfWidth)) return [...points];
  const ahead = unit(bend, next);
  const back = leadIn(bend, ahead, halfWidth, block, overlap);
  if (back === null) return [...points];
  return [[bend[0] - ahead[0] * back, bend[1] - ahead[1] * back], ...points.slice(2)];
}

/** The piece's points with each end that turns at a gate face led in along its next leg. */
export function abutGateBlocks(
  points: readonly Vec2[],
  blocks: readonly GateBlock[],
  halfWidth: number,
  overlap: number
): Vec2[] {
  const head = abutStart(points, blocks, halfWidth, overlap);
  return abutStart([...head].reverse(), blocks, halfWidth, overlap).reverse();
}
